/**
 * @file ScheduleSessionDialog.tsx
 * @brief Create or edit a scheduled interpretation session (FormSheet).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Languages, LoaderCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { GenderPicker } from "@/components/ui/GenderPicker";
import { Input } from "@/components/ui/Input";
import { LanguagePicker } from "@/components/ui/LanguagePicker";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import type { CompanyEmployee } from "@/features/employees/schemas";
import { useEmployeesList } from "@/features/employees/api";
import { useCreateScheduledSession, useUpdateScheduledSession } from "../api";
import type { ScheduledSession } from "../schemas";
import { ScheduleEmployeePicker } from "./ScheduleEmployeePicker";

/** The next whole 15 minutes from now: a fresh booking's starting time. */
function defaultScheduledAt(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil((d.getMinutes() + 1) / 15) * 15);
  return d.toISOString();
}

function employeeGender(employee: CompanyEmployee | null | undefined): string {
  const gender = employee?.gender;
  return typeof gender === "string" ? gender : "";
}

interface ScheduleSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  session?: ScheduledSession | null;
  preselectedEmployeeId?: string | null;
}

export function ScheduleSessionDialog({
  open,
  onOpenChange,
  mode,
  session,
  preselectedEmployeeId,
}: ScheduleSessionDialogProps) {
  const employees = useEmployeesList();
  const create = useCreateScheduledSession();
  const update = useUpdateScheduledSession();

  const [employee, setEmployee] = useState<CompanyEmployee | null>(null);
  // One date-and-time value (DES 01-10-2026: "same component use karo jo
  // hum nay banaya tha for date and time together"), was a date picker and
  // a native time input.
  const [scheduledAt, setScheduledAt] = useState<string | undefined>(defaultScheduledAt);
  const [sourceLang, setSourceLang] = useState("");
  const [targetLang, setTargetLang] = useState("");
  const [personBName, setPersonBName] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [personAGender, setPersonAGender] = useState("");
  const [personBGender, setPersonBGender] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  // Three sections, so tabs (DES 01-10-2026: form standard).
  const [tab, setTab] = useState<ScheduleSessionTab>("participant");

  const applyEmployee = useCallback((next: CompanyEmployee | null) => {
    setEmployee(next);
    setPersonAGender(employeeGender(next));
  }, []);

  useEffect(() => {
    if (!open) return;

    setServerError(null);
    setTab("participant");

    if (mode === "edit" && session) {
      setScheduledAt(session.scheduled_at);
      setSourceLang(session.source_language ?? "");
      setTargetLang(session.target_language ?? "");
      setPersonBName(session.person_b_name ?? "");
      setReferenceNumber(session.reference_number ?? "");
      setPersonAGender(session.person_a_gender ?? "");
      setPersonBGender(session.person_b_gender ?? "");
      setEmployee(
        session.participant_id
          ? {
              id: session.participant_id,
              email: session.participant_email,
              first_name: session.participant_first_name ?? "",
              last_name: session.participant_last_name ?? "",
              role: "user",
              status: "active",
            }
          : null,
      );
      return;
    }

    setScheduledAt(defaultScheduledAt());
    setSourceLang("en-US");
    setTargetLang("");
    setPersonBName("");
    setReferenceNumber("");
    setPersonAGender("");
    setPersonBGender("");
    setEmployee(null);
  }, [open, mode, session]);

  useEffect(() => {
    if (!open || mode !== "create" || !preselectedEmployeeId) return;
    const match = employees.data?.find((e) => e.id === preselectedEmployeeId);
    if (match) applyEmployee(match);
  }, [open, mode, preselectedEmployeeId, employees.data, applyEmployee]);

  useEffect(() => {
    if (!open || !employee?.id || !employees.data?.length) return;
    const full = employees.data.find((e) => e.id === employee.id);
    if (!full) return;

    setEmployee((prev) => (prev?.id === full.id ? { ...prev, ...full } : prev));

    const profileGender = employeeGender(full);
    if (profileGender) {
      setPersonAGender((current) => current || profileGender);
    }
  }, [open, employee?.id, employees.data]);

  const pending = create.isPending || update.isPending;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    if (!employee) {
      setServerError("Select an employee for this session.");
      setTab("participant");
      return;
    }
    if (!sourceLang || !targetLang) {
      setServerError("Select both source and target languages.");
      setTab("languages");
      return;
    }
    if (!scheduledAt) {
      setServerError("Pick a date and time.");
      setTab("when");
      return;
    }
    // No bookings in the past. An edit that keeps a session's original
    // (already gone) time is allowed, so its other details can still change.
    const unchanged = mode === "edit" && session?.scheduled_at === scheduledAt;
    if (!unchanged && new Date(scheduledAt).getTime() <= Date.now()) {
      setServerError("Pick a time in the future.");
      setTab("when");
      return;
    }

    const payload = {
      participantId: employee.id,
      participantEmail: employee.email,
      person_b_name: personBName.trim() || undefined,
      reference_number: referenceNumber.trim() || undefined,
      person_a_gender: personAGender || undefined,
      person_b_gender: personBGender || undefined,
      sourceLang,
      targetLang,
      scheduledAt,
    };

    try {
      if (mode === "edit" && session) {
        await update.mutateAsync({ id: session.id, input: payload });
        toast({
          variant: "success",
          title: "Session updated",
          description: "The participant will be notified by email.",
        });
      } else {
        await create.mutateAsync(payload);
        toast({
          variant: "success",
          title: "Session scheduled",
          description: `${employee.email} will receive a confirmation email.`,
        });
      }
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : "Please try again.";
      setServerError(message);
    }
  };

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Cmd+K-style palette frame (DES 01-10-2026: form standard). */}
      <FormSheetContent side="palette">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>
                {mode === "edit" ? "Edit session" : "Schedule session"}
              </FormSheetTitle>
              <FormSheetDescription>
                Book an interpretation appointment for a team member.
              </FormSheetDescription>
            </FormSheetHeader>

            <FormSheetTabs
              tabs={SCHEDULE_SESSION_TABS}
              value={tab}
              onChange={setTab}
              label={mode === "edit" ? "Edit session" : "Schedule session"}
            />

            <FormSheetBody>
              <div {...formSheetTabPanel(tab, "participant")}>
                <FormSheetSection
                  hideHeading
                  title="Participant"
                  subtitle="Who this interpretation session is for."
                >
                  <FormSheetFields>
                    {/* Employee and the other party side by side, genders
                      under them (DES 01-10-2026: form standard). */}
                    <FormSheetField>
                      <FormFieldGroup label="Employee" required>
                        <ScheduleEmployeePicker
                          value={employee?.id ?? preselectedEmployeeId ?? null}
                          onChange={applyEmployee}
                          disabled={mode === "edit"}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Other participant name (optional)" htmlFor="person-b">
                        <Input
                          capitalizeFirst
                          id="person-b"
                          value={personBName}
                          onChange={(e) => setPersonBName(e.target.value)}
                          placeholder="e.g. Client name"
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup
                        label="Employee gender"
                        hint="Optional · prefilled from the employee profile when available."
                      >
                        <GenderPicker
                          value={personAGender || null}
                          onChange={(next) => setPersonAGender(next ?? "")}
                          sheetSubtitle="Prefilled from the employee profile when available."
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup
                        label="Other participant gender"
                        hint="Optional · voice hint for the other party in the call."
                      >
                        <GenderPicker
                          value={personBGender || null}
                          onChange={(next) => setPersonBGender(next ?? "")}
                          sheetSubtitle="Optional voice hint for the other party in the call."
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "when")}>
                <FormSheetSection
                  hideHeading
                  title="When"
                  subtitle="Date and time for the appointment."
                >
                  {/* Date & time (the date popover with time, as Schedule for
                      later) and the reference on one row. */}
                  <FormSheetFields>
                    <FormSheetField>
                      <FormFieldGroup label="Date & time" required>
                        <DateTimePicker
                          value={scheduledAt ?? null}
                          onChange={setScheduledAt}
                          title="Session start"
                          disablePast
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Reference number (optional)" htmlFor="reference">
                        <Input
                          id="reference"
                          value={referenceNumber}
                          onChange={(e) => setReferenceNumber(e.target.value)}
                          placeholder="Case or booking reference"
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "languages")}>
                <FormSheetSection
                  hideHeading
                  title="Languages"
                  subtitle="Source and target languages for the session."
                >
                  <FormSheetFields>
                    <FormSheetField>
                      <FormFieldGroup label="Source language" required>
                        <LanguagePicker
                          value={sourceLang}
                          onChange={(next) => setSourceLang(next ?? "")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Target language" required>
                        <LanguagePicker
                          value={targetLang}
                          onChange={(next) => setTargetLang(next ?? "")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              {serverError ? (
                <FormSheetFields className="grid-cols-1">
                  <FormSheetAlert>{serverError}</FormSheetAlert>
                </FormSheetFields>
              ) : null}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={pending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={pending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {pending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : mode === "edit" ? (
                    "Save changes"
                  ) : (
                    "Schedule session"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

type ScheduleSessionTab = "participant" | "when" | "languages";

const SCHEDULE_SESSION_TABS: FormSheetTab<ScheduleSessionTab>[] = [
  { id: "participant", label: "Participant", icon: Users },
  { id: "when", label: "When", icon: CalendarClock },
  { id: "languages", label: "Languages", icon: Languages },
];
